import React from "react";
import brand from "../../assets/images/megapark.png"; // sağ alttaki watermark/logo
import { API_BASE, toUrl } from "../../lib/config";

type IntroRow = { id: string; orderIndex?: number; title?: string | null; text?: string | null };
type IntroPayload = {
  kicker?: string | null;
  displayGreen?: string | null;
  displayDark?: string | null;
  lede?: string | null;
  watermarkUrl?: string | null; // UUID veya tam/relatif URL
  rows?: IntroRow[];
};

const DoctorsIntro: React.FC = () => {

  const ImgWithFallback: React.FC<{ src?: string; alt?: string; className?: string; style?: React.CSSProperties }> = ({ src, alt = '', className, style }) => {
    const input = src || '';
    // Normalize: if not absolute and not an /api/ path, assume UUID and build /api/media/:id
    const raw = /^https?:\/\//i.test(input)
      ? input
      : (input.startsWith('/api/') ? input : `/api/media/${input}`);
    const abs = /^https?:\/\//i.test(raw) ? raw : toUrl(raw);
    const [current, setCurrent] = React.useState(abs);
    return (
      <img
        src={current}
        alt={alt}
        className={className}
        style={style}
        crossOrigin="anonymous"
        onError={(e) => {
          const cur = e.currentTarget.getAttribute('src') || '';
          const next = cur === abs ? raw : abs;
          if (next && next !== cur) e.currentTarget.src = next;
        }}
      />
    );
  };

  const [intro, setIntro] = React.useState<IntroPayload>({});

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch(toUrl('/api/doctors-intro'));
        if (!res.ok) throw new Error('fetch_failed');
        const data = await res.json();
        const obj: IntroPayload = Array.isArray(data) ? (data[0] || {}) : (data || {});
        if (!cancelled) setIntro(obj || {});
      } catch {
        // sessizce fallback kullan
      }
    };
    run();
    return () => { cancelled = true; };
  }, [API_BASE]);

  const rows = React.useMemo(() => {
    const list = Array.isArray(intro.rows) ? intro.rows.slice().sort((a,b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)) : [];
    return list;
  }, [intro.rows]);

  const row0 = rows[0];
  const row1 = rows[1];

  return (
    <section className="doctors-intro">
      <div className="container">
        {/* Üst metinler */}
        <header className="intro-head">
          <p className="kicker">
            {intro.kicker ?? 'Hekim Kadromuz'}
            <span className="kicker-accent" aria-hidden="true" />
          </p>

          <h1 className="display">
            <span className="display-green">{intro.displayGreen ?? 'Sağlığın ve Güvenin'}</span>
            <span className="display-dark">{intro.displayDark ?? 'Adresi'}</span>
          </h1>

          <p className="lede">
            {intro.lede ?? (
              <>Hastanenin uzman doktor kadrosu, hasta odaklı yaklaşımı ve yüksek
              mesleki yetkinliği ile sağlık hizmetlerinde fark yaratmaktadır. Her biri
              alanında deneyimli olan hekimler, en güncel tıbbi gelişmeleri takip
              ederek hastalarına güvenilir ve etkili çözümler sunmaktadır. Ekip,
              multidisipliner bir anlayışla çalışarak, her hastaya özel ve kapsamlı bir
              bakım sağlamayı hedeflemektedir.</>
            )}
          </p>

          {/* Watermark / logo */}
          <ImgWithFallback className="brand-watermark" src={intro.watermarkUrl || brand} alt="Megapark Hospital" />
        </header>

        <hr className="sep" />

        {/* İkonlu 2 kolon */}
        <div className="feature-rows">
          <article className="feature">
            <div className="icon">
              <i className="bi bi-person-badge" aria-hidden="true" />
            </div>
            <div className="content">
              <h3 className="title">{row0?.title ?? 'Profesyonel Ekip'}</h3>
              <p className="text">{row0?.text ?? 'Küresel ölçekte, çoklu ortam tabanlı iş birliğini ve fikir paylaşımını teşvik ediyoruz. Sürekli olarak üstün fırsatlar sunmaya odaklanıyoruz.'}</p>
            </div>
          </article>

          <article className="feature">
            <div className="icon">
              <i className="bi bi-heart-pulse" aria-hidden="true" />
            </div>
            <div className="content">
              <h3 className="title">{row1?.title ?? 'Hizmetler ve Teknoloji'}</h3>
              <p className="text">{row1?.text ?? 'Üst düzey analizler sunmak için çevik çalışma yöntemlerini kullanıyoruz. Yenilikçi yaklaşımlarla tedavi süreçlerinizi güvenle geliştiriyoruz.'}</p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default DoctorsIntro;
